import { useState, useMemo } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Alert, CircularProgress, Box, Typography,
  List, ListItemButton, ListItemText, Chip, Divider,
} from '@mui/material'
import { ContentCopy } from '@mui/icons-material'
import StatusBadge from '../common/StatusBadge'

export default function CopyPlanModal({
  open, onClose, onSubmit, plans, loading, error, currentPlant,
}) {
  const [sourcePlanId, setSourcePlanId] = useState(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const sourcePlan = useMemo(
    () => plans.find((p) => p.id === sourcePlanId),
    [plans, sourcePlanId],
  )

  const handleSelectSource = (plan) => {
    setSourcePlanId(plan.id)
    setName(`${plan.name} — Copy`)
    setDescription(plan.description || '')
  }

  const valid = sourcePlanId && name.trim().length > 0

  const handleSubmit = () => {
    if (!valid) return
    onSubmit({ sourcePlanId, name: name.trim(), description: description.trim(), plant: sourcePlan?.plant || currentPlant || 'FBO' })
  }

  const handleClose = () => {
    setSourcePlanId(null)
    setName('')
    setDescription('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Modification / Copy from Plan</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}

        <Typography variant="subtitle2" color="text.secondary">
          Select a source plan
        </Typography>

        <Box sx={{ maxHeight: 240, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <List dense disablePadding>
            {plans.map((p) => (
              <ListItemButton
                key={p.id}
                selected={sourcePlanId === p.id}
                onClick={() => handleSelectSource(p)}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }} noWrap>
                        {p.name}
                      </Typography>
                      <Chip label={p.plant} size="small" variant="outlined" />
                      <StatusBadge status={p.status === 'APPROVED' ? 'APPROVED' : 'DRAFT'} />
                    </Box>
                  }
                  secondary={`Modifications: ${p.modificationCount ?? 0}`}
                />
              </ListItemButton>
            ))}
            {plans.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No plans available</Typography>
              </Box>
            )}
          </List>
        </Box>

        {sourcePlan && (
          <>
            <Divider />
            <TextField
              label="New Plan Name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!valid || loading}
          startIcon={loading ? <CircularProgress size={18} /> : <ContentCopy />}
        >
          Create Copy
        </Button>
      </DialogActions>
    </Dialog>
  )
}
